import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, LogOut, RefreshCw, Phone, MapPin, Car, Loader2, ArrowLeft, Download, CheckCircle2, RotateCcw, Trash2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO, waLink } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dg_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
  }, []);

  const loadLeads = useCallback(async (tk) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads?limit=300`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) loadLeads(token);
  }, [token, loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || "Falha no login");
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const toggleStatus = async (lead) => {
    const next = lead.status === "atendido" ? "novo" : "atendido";
    setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: next } : l)));
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { status: next }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: lead.status } : l)));
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Excluir o pedido de ${lead.name}? Esta ação não pode ser desfeita.`)) return;
    const prev = leads;
    setLeads((ls) => ls.filter((l) => l.id !== lead.id));
    try {
      await axios.delete(`${API}/leads/${lead.id}`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      if (e.response?.status === 401) logout();
      else setLeads(prev);
    }
  };

  const exportCsv = () => {
    const headers = ["Data", "Nome", "Telefone", "Veiculo", "Localizacao", "Mensagem", "Status"];
    const rows = leads.map((l) => [fmtDate(l.created_at), l.name, l.phone, l.vehicle || "", l.location || "", (l.message || "").replace(/\n/g, " "), l.status || "novo"]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pedidos-dg-guinchos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ink text-white grid place-items-center px-5 grain relative">
        <div className="relative z-10 w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-brand mb-6"><ArrowLeft className="h-4 w-4" /> Voltar ao site</Link>
          <div className="bg-surface border border-white/10 rounded-sm p-8">
            <img src={LOGO} alt="DG Guinchos" className="h-14 w-auto mx-auto" />
            <div className="flex items-center gap-2 justify-center mt-6 text-brand">
              <Lock className="h-5 w-5" />
              <h1 className="font-display text-2xl font-black uppercase">Painel de Pedidos</h1>
            </div>
            <form onSubmit={login} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-zinc-400">E-mail</Label>
                <Input id="email" data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-ink border-white/15 focus-visible:ring-brand focus-visible:border-brand" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-zinc-400">Senha</Label>
                <Input id="password" data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-ink border-white/15 focus-visible:ring-brand focus-visible:border-brand" required />
              </div>
              {error && <p data-testid="admin-error" className="text-sm text-brand">{error}</p>}
              <button type="submit" disabled={busy} data-testid="admin-login-btn" className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-display font-bold uppercase tracking-wider py-3.5 rounded-sm transition-all active:scale-95">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />} Entrar
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="border-b border-white/10 sticky top-0 bg-black/85 backdrop-blur-xl z-10">
        <div className="container-x h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="DG Guinchos" className="h-10 w-auto" />
            <span className="font-display text-xl font-bold uppercase hidden sm:block">Pedidos do Site</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} disabled={!leads.length} data-testid="admin-export-csv" className="inline-flex items-center gap-2 border border-white/15 hover:border-wapp disabled:opacity-40 px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider transition-colors">
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">Baixar CSV</span>
            </button>
            <button onClick={() => loadLeads(token)} data-testid="admin-refresh" className="inline-flex items-center gap-2 border border-white/15 hover:border-brand px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider transition-colors">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Atualizar</span>
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider transition-colors">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        <div className="flex items-baseline justify-between mb-6">
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase">Solicitações recebidas</h1>
          <span data-testid="admin-count" className="text-brand font-display text-2xl font-bold">{leads.length}</span>
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center text-zinc-500"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : leads.length === 0 ? (
          <div className="py-20 text-center text-zinc-500 border border-dashed border-white/15 rounded-sm">Nenhum pedido recebido ainda.</div>
        ) : (
          <div className="grid gap-3" data-testid="admin-leads">
            {leads.map((l) => (
              <div key={l.id} className={`border rounded-sm p-5 flex flex-col md:flex-row md:items-center gap-4 transition-colors ${l.status === "atendido" ? "bg-surface/50 border-wapp/30" : "bg-surface border-white/10"}`}>
                <div className="md:w-40 shrink-0 text-xs text-zinc-500">{fmtDate(l.created_at)}</div>
                <div className="flex-1 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-xl font-bold text-white">{l.name}</span>
                    {l.status === "atendido" ? (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-wapp border border-wapp/40 rounded-sm px-2 py-0.5">Atendido</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider font-bold text-brand border border-brand/40 rounded-sm px-2 py-0.5">Novo</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-zinc-300"><Phone className="h-4 w-4 text-brand" /> {l.phone}</div>
                  {l.vehicle && <div className="flex items-center gap-2 text-zinc-400 text-sm"><Car className="h-4 w-4 text-brand" /> {l.vehicle}</div>}
                  {l.location && <div className="flex items-center gap-2 text-zinc-400 text-sm"><MapPin className="h-4 w-4 text-brand" /> {l.location}</div>}
                  {l.message && <div className="sm:col-span-2 text-sm text-zinc-400 mt-1">{l.message}</div>}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <button onClick={() => toggleStatus(l)} data-testid={`lead-toggle-${l.id}`} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-semibold transition-colors ${l.status === "atendido" ? "border border-white/15 text-zinc-300 hover:border-brand" : "bg-wapp hover:bg-wapp-hover text-black"}`}>
                    {l.status === "atendido" ? <><RotateCcw className="h-4 w-4" /> Reabrir</> : <><CheckCircle2 className="h-4 w-4" /> Atendido</>}
                  </button>
                  <a href={`tel:${l.phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-1.5 border border-white/15 hover:border-brand px-3 py-2 rounded-sm text-sm transition-colors"><Phone className="h-4 w-4" /> Ligar</a>
                  <a href={`https://wa.me/55${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-wapp hover:bg-wapp-hover text-black px-3 py-2 rounded-sm text-sm font-semibold transition-colors"><WhatsAppIcon className="h-4 w-4" /> WhatsApp</a>
                  <button onClick={() => deleteLead(l)} data-testid={`lead-delete-${l.id}`} aria-label="Excluir pedido" className="inline-flex items-center gap-1.5 border border-white/15 hover:border-brand hover:text-brand text-zinc-400 px-3 py-2 rounded-sm text-sm transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
